import * as React from "react";
import { useModal } from "../../../../../utils/custom-hooks";
import { ServiceProviderTemplate } from "../../../../template/service-provider/default-template";
import { displayName } from "../../../../../application/domain/rbac/rbac";
import {
  OrganizationUserServiceProps,
  OrganizationUserService,
} from "../../../../../application/service/service-provider/user-service";
import { connectPageService } from "../../../../../application/domain/base/base-application-service";
import {
  useHistory,
  useLocation,
  useParams,
  Link as RouterLink,
} from "react-router-dom";
import { ServiceProviderTabs } from "../../../../molecules/service-provider-tab";
import { ServiceProviderR } from "../../routes";
import { parseQueryOfLocation } from "../../../../../utils/url-utils";
import { useEffect } from "react";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { Dropdown } from "../../../../molecules/dropdown";
import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { BatchImportCreateUserModal } from "../../../../organisms/settings/user-list/batch-import-create-user-modal";
import { BatchImportUpdateUserModal } from "../../../../organisms/settings/user-list/batch-import-update-user-modal";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import { Pagination, Spinner, Link } from "@graffer-inc/console-design-kit";
import { ToggleTag } from "../../../../organisms/common/toggle-tag";
import { UserCsvDownloadModal } from "../../../../organisms/settings/user-list/user-csv-download-modal";

export const OrganizationUserList: React.FC<OrganizationUserServiceProps> = ({
  isLoading,
  isBatchImporting,
  organizationUsers,
  paging,
  query,
  setPage,
  movePage,
  setQuery,
  fetchUsersByOrganizationId,
  searchUsers,
  importByCsv,
  updateByCsv,
  onDownloadCsv,
  downloadTemplateCsv,
}) => {
  const { page = "1", q } = parseQueryOfLocation(useLocation()) as {
    page: string;
    q: string | undefined;
  };
  const { orgId } = useParams<{ orgId: string }>();
  const history = useHistory();
  const csvImportModal = useModal();
  const csvUpdateModal = useModal();
  const csvDownloadModal = useModal();
  const dropdownModal = useModal();

  useEffect(() => {
    setPage(parseInt(page, 10));
    setQuery(q || "");
    fetchUsersByOrganizationId(orgId);
  }, [page, q, orgId, fetchUsersByOrganizationId, setPage, setQuery]);

  const getOrgUserDetailLink = React.useCallback(
    (userId: string) => ServiceProviderR.OrgUserDetail.link({ orgId, userId }),
    [orgId],
  );

  if (!organizationUsers) {
    return <div />;
  }

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <Spinner loading={isLoading} />
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={orgId} />
            <nav className="level">
              <div className="level-left">
                <div className="level-item">
                  <div className="field has-addons">
                    <p
                      className="control has-icons-left has-icons-right"
                      style={{ minWidth: "280px" }}
                    >
                      <input
                        className="input"
                        placeholder="アカウント名、氏名で検索"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{
                          fontSize: "14px",
                        }}
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-search" />
                      </span>
                      <span
                        className="icon is-small is-right"
                        role="button"
                        onClick={() => {
                          setQuery("");
                          searchUsers();
                        }}
                        style={{ pointerEvents: "initial", cursor: "pointer" }}
                      >
                        <i className="fas fa-times" />
                      </span>
                    </p>
                    <p className="control">
                      <SecondaryButton text="検索" onClick={searchUsers} />
                    </p>
                  </div>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <SecondaryButton
                    text={"CSV出力"}
                    onClick={csvDownloadModal.open}
                  />
                </div>
                <div className="level-item">
                  <OutsideClickHandler onOutsideClick={dropdownModal.close}>
                    <Dropdown
                      title="一括操作"
                      isActive={dropdownModal.isActive}
                      onClick={dropdownModal.open}
                      menus={[
                        {
                          title: "一括登録",
                          onClick: csvImportModal.open,
                        },
                        {
                          title: "一括更新",
                          onClick: csvUpdateModal.open,
                        },
                      ]}
                    />
                  </OutsideClickHandler>
                </div>
                <div className="level-item">
                  <PrimaryButton
                    text={"新規登録"}
                    onClick={() =>
                      history.push(ServiceProviderR.OrgUsersNew.link({ orgId }))
                    }
                  />
                </div>
              </div>
            </nav>
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>ユーザーID</th>
                  <th>アカウント名</th>
                  <th>氏名</th>
                  <th>ロール</th>
                  <th>2段階認証</th>
                  <th>メールアドレス</th>
                  <th>ユーザーステータス</th>
                </tr>
              </thead>
              <tbody>
                {organizationUsers.map((orgUser) => (
                  <tr key={orgUser.id} className="c-tr-has-tag">
                    <td>
                      <div className="c-word-break-all">
                        <Link
                          anchor={({ ...props }) => (
                            <RouterLink
                              {...props}
                              to={getOrgUserDetailLink(orgUser.id)}
                            />
                          )}
                        >
                          {convertToFormattedId(orgUser.id)}
                        </Link>
                      </div>
                    </td>
                    <td>
                      <div className="c-word-break-all">
                        {orgUser.accountName}
                      </div>
                    </td>
                    <td>
                      <div className="c-word-break-all">{orgUser.name}</div>
                    </td>
                    <td>
                      {orgUser.roles.map((role, i) => (
                        <div
                          className="c-word-break-all"
                          key={`${orgUser.id}-role${i}`}
                        >
                          {displayName(role)}
                        </div>
                      ))}
                    </td>
                    <td>{orgUser.isTOTPEnabled() ? "ON" : "OFF"}</td>
                    <td>{orgUser.email ? orgUser.email : "未登録"}</td>
                    <td>
                      <ToggleTag disabled={orgUser.disabled} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "16px" }}>
              <Pagination
                onPageClick={movePage}
                currentPage={paging.current}
                lastPage={paging.total}
              />
            </div>
          </div>
          {csvImportModal.isActive && (
            <BatchImportCreateUserModal
              orgId={orgId}
              isBatchImporting={isBatchImporting}
              isActive={csvImportModal.isActive}
              onClose={csvImportModal.close}
              onImport={importByCsv}
              onDownloadTemplateCsv={downloadTemplateCsv}
              fetchUsersByOrganizationId={fetchUsersByOrganizationId}
            />
          )}
          {csvUpdateModal.isActive && (
            <BatchImportUpdateUserModal
              orgId={orgId}
              isBatchImporting={isBatchImporting}
              isActive={csvUpdateModal.isActive}
              onClose={csvUpdateModal.close}
              onImport={updateByCsv}
            />
          )}
          {csvDownloadModal.isActive && (
            <UserCsvDownloadModal
              isActive={csvDownloadModal.isActive}
              onClose={csvDownloadModal.close}
              onDownload={(isOutputUserId) =>
                onDownloadCsv(orgId, isOutputUserId)
              }
            />
          )}
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

export const OrganizationUserListContainer = connectPageService(
  OrganizationUserList,
  () => new OrganizationUserService(),
);
